// plugins/api-client.ts

import axios from 'axios'
import { Capacitor } from '@capacitor/core'
import { MwApi, Configuration } from '~/generated-api-client' // Adjust the path to your generated client

export default defineNuxtPlugin((nuxtApp) => {
  const config = useRuntimeConfig()
  const apiBase = (config.public.apiBase ? config.public.apiBase.replace(/\/$/, '') : '') || '/api'
  const apiBasePath = `${apiBase}/mw`

  // Check if running in Capacitor (mobile app)
  const isCapacitor = Capacitor.isNativePlatform()

  const event = nuxtApp.ssrContext?.event

  // On auth-optional routes (the checkout pages) a 401 must NOT trigger the
  // global redirect to /login — the onboarding modal handles it. Prefix list
  // lives in ~/utils/auth.
  const isAuthOptionalRoute = () => {
    const path = import.meta.server
      ? (nuxtApp.ssrContext?.event?.node?.req?.url as string) || ''
      : (typeof window !== 'undefined' ? window.location.pathname : '')
    return isAuthOptionalPath(path)
  }

  const resolveServerBaseURL = () => {
    // For client-side (no event), use relative path - browser will resolve it automatically
    if (!event) {
      return apiBasePath
    }

    // During SSR, make internal requests to the Nuxt server's BFF route
    // The BFF handles authentication, session management, and proxying to the backend
    // Always use loopback for internal SSR requests.
    // External hostnames (from Cloudflare/NGINX) won't resolve inside Docker containers.
    const hostname = '127.0.0.1'

    // In production Nitro uses NITRO_PORT, while dev uses NUXT_PORT.
    const port = String(process.env.NITRO_PORT || process.env.NUXT_PORT || process.env.PORT || 3000)
    
    // Always use http for internal SSR requests (not https)
    // The BFF route will handle proxying to the backend with proper auth
    return `http://${hostname}:${port}${apiBasePath}`
  }

  // During SSR, use internal URL to go through BFF
  // On client-side web, use relative path (will be resolved by the browser)
  // In Capacitor, use the remote BFF URL so the mobile app also goes through the BFF
  const bffBaseUrl = (config.public.bffUrl || '').replace(/\/$/, '')
  const baseURL = isCapacitor
    ? `${bffBaseUrl}${apiBasePath}`
    : (event ? resolveServerBaseURL() : apiBasePath)

  const axiosInstance = axios.create({
    baseURL,
    headers: {
      'Content-Type': 'application/json'
    },
    withCredentials: !isCapacitor, // Only use cookies in web mode
  })

  // In Capacitor mode, send the BFF session id as a bearer token instead of cookies
  if (isCapacitor) {
    const { getSessionId } = useBffSession()

    axiosInstance.interceptors.request.use(
      async (config) => {
        const sessionId = await getSessionId()

        if (sessionId) {
          if (!config.headers) {
            config.headers = {} as any
          }
          ;(config.headers as any).Authorization = `Bearer ${sessionId}`
        }

        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )
  }

  axiosInstance.interceptors.response.use(
    (response) => {
      return response
    },
    async (error) => {
      if (error.response?.status === 401) {
        // On checkout routes the global auth middleware owns the redirect (to
        // sign-up), so don't let the interceptor send these to /login.
        if (isAuthOptionalRoute()) {
          return Promise.reject(error)
        }

        // A 401 from an endpoint that ANSWERS a credential is that answer, not
        // an expired session — the calling page shows it and stays put.
        // Redirecting instead threw a customer off /register mid-signup the
        // moment they mistyped the emailed two-factor code.
        if (isAuthAnswerPath(error.config?.url)) {
          return Promise.reject(error)
        }

        // Preserve the page the user was on so login can return them there.
        const currentPath = import.meta.server
          ? (nuxtApp.ssrContext?.event?.node?.req?.url as string) || ''
          : (typeof window !== 'undefined' ? window.location.pathname + window.location.search : '')
        const onAuthPage = currentPath.startsWith('/login') || currentPath.startsWith('/register')
        const loginUrl = (!currentPath || onAuthPage)
          ? '/login'
          : `/login?redirect=${encodeURIComponent(currentPath)}`

        if (isCapacitor) {
          const { clearSession } = useBffSession()
          await clearSession()
          navigateTo(loginUrl)
          return Promise.reject(error)
        }

        // In SSR mode or normal web mode
        if (nuxtApp.ssrContext) {
          nuxtApp.ssrContext.event.node.res.writeHead(302, { Location: loginUrl })
          nuxtApp.ssrContext.event.node.res.end()
        }
        else {
          navigateTo(loginUrl)
        }
      }

      return Promise.reject(error)
    }
  )

  if (event?.node.req.headers.cookie) {
    axiosInstance.defaults.headers.common.Cookie = event.node.req.headers.cookie
    axiosInstance.defaults.headers.common.cookie = event.node.req.headers.cookie
  }

  if (import.meta.server && event) {
    reportUpstreamTimings(nuxtApp, axiosInstance)
  }

  const configuration = new Configuration({
    basePath: apiBasePath,
  })

  const apiClient = new MwApi(configuration, apiBasePath, axiosInstance)

  return {
    provide: {
      mwApi: apiClient
    }
  }
})


/**
 * Says, on the server-rendered document itself, which API calls the render made
 * and how long each took — as `Server-Timing`, the header a browser's network
 * panel already knows how to draw.
 *
 * A server render waits for every read the guard, the layout and the page make,
 * and none of them is visible from outside: the dashboard spent five seconds
 * before its first byte on three identical `/whmcs/me` calls made one after the
 * other, and the only symptom was "the page is slow". This is what makes that
 * countable — by a person in devtools, and by
 * `tools/e2e/console-load-battle.mjs`, which fails when a page view starts
 * resolving the signed-in customer more than once again.
 *
 * It describes the customer's own requests to the customer who made them: paths
 * of the public API their browser calls anyway, and durations. No query string,
 * no body, no header, and nothing about a request that was not theirs.
 */
function reportUpstreamTimings(
  // The instance a plugin is handed, before other plugins have augmented it —
  // which is all this needs: its hooks.
  nuxtApp: Pick<ReturnType<typeof useNuxtApp>, 'hook'>,
  axiosInstance: ReturnType<typeof axios.create>,
) {
  const MAX_ENTRIES = 24
  const calls: { label: string; ms: number }[] = []
  const startedAt = new WeakMap<object, number>()

  axiosInstance.interceptors.request.use((config) => {
    startedAt.set(config, performance.now())
    return config
  })

  const record = (config: any, status: number | string) => {
    const started = config && startedAt.get(config)
    if (started === undefined || calls.length >= MAX_ENTRIES) return
    const path = String(config.url || '').split('?')[0]
    const label = `${String(config.method || 'get').toUpperCase()} ${path} ${status}`
    // A header value: printable ASCII only, and nothing that ends the quoting.
    calls.push({ label: label.replace(/[^\x20-\x7e]|["\\]/g, ''), ms: performance.now() - started })
  }

  axiosInstance.interceptors.response.use(
    (response) => {
      record(response.config, response.status)
      return response
    },
    (error) => {
      record(error?.config, error?.response?.status ?? 'ERR')
      return Promise.reject(error)
    },
  )

  nuxtApp.hook('app:rendered', ({ ssrContext }) => {
    const target = ssrContext?.event
    if (!target || calls.length === 0 || target.node.res.headersSent) return
    const value = calls
      .map((call, index) => `api${index + 1};desc="${call.label}";dur=${call.ms.toFixed(1)}`)
      .join(', ')
    // The raw Node response, on purpose: this plugin runs in the browser too,
    // and an `h3` import here would travel into the client bundle for a branch
    // that never executes there.
    const existing = target.node.res.getHeader('Server-Timing')
    target.node.res.setHeader('Server-Timing', existing ? `${existing}, ${value}` : value)
  })
}
